import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';

export interface SelectOption {
  value: string;
  label: string;
}

interface FilterableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required?: boolean;
  helperText?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  sx?: Record<string, unknown>;
  /** Enable free-text entry (user can type custom values not in the list) */
  freeSolo?: boolean;
  /** Called as user types — use for server-side search (e.g. taxonomy) */
  onInputChange?: (inputValue: string) => void;
  /** Show loading spinner in dropdown while options are being fetched */
  loading?: boolean;
}

/**
 * Filterable dropdown that sorts options alphabetically and lets users
 * type to filter. Wraps MUI Autocomplete for consistent UX across the app.
 */
export function FilterableSelect({
  options,
  value,
  onChange,
  label,
  placeholder,
  size = 'small',
  fullWidth = true,
  required = false,
  helperText,
  allowEmpty = true,
  emptyLabel = 'None',
  sx,
  freeSolo = false,
  onInputChange,
  loading = false,
}: FilterableSelectProps) {
  const sortedOptions = useMemo(() => {
    const sorted = [...options].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
    if (allowEmpty && !freeSolo) {
      return [{ value: '', label: emptyLabel }, ...sorted];
    }
    return sorted;
  }, [options, allowEmpty, emptyLabel, freeSolo]);

  const selected = sortedOptions.find((o) => o.value === value) ?? null;

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      options={sortedOptions}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      value={freeSolo ? (selected ?? (value || null)) : selected}
      onChange={(_, newValue) => {
        if (newValue === null) {
          onChange('');
        } else if (typeof newValue === 'string') {
          // freeSolo: user typed and pressed Enter
          onChange(newValue);
        } else {
          onChange(newValue.value);
        }
      }}
      onInputChange={
        onInputChange
          ? (_, val, reason) => {
              if (reason === 'input') onInputChange(val);
            }
          : undefined
      }
      isOptionEqualToValue={(option, val) => {
        if (typeof option === 'string' || typeof val === 'string') {
          const optVal = typeof option === 'string' ? option : option.value;
          const valVal = typeof val === 'string' ? val : val.value;
          return optVal === valVal;
        }
        return option.value === val.value;
      }}
      freeSolo={freeSolo}
      loading={loading}
      filterOptions={
        onInputChange
          ? // When using server-side search, don't double-filter client-side
            (opts) => opts
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText}
        />
      )}
      sx={sx}
    />
  );
}
